import React, { FC } from "react";
import classNames from "classnames";

import { Button } from "../Button";
import Icon from "../Icon";

type Props = {
  loading: boolean;
  page: number;
  pageCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onChange: (page: number) => void;
};

type MyArray = [...(number | null)[]];

const PAGINATION_SLOTS = 7;
const MID_SLOT_CEIL = Math.ceil(PAGINATION_SLOTS / 2);
const MID_SLOT_FLOOR = Math.floor(PAGINATION_SLOTS / 2);

const getLeftPageNumbers = (page: number, pages: number): MyArray => {
  const pageGap = pages - page;
  const placesForLeft =
    PAGINATION_SLOTS - Math.min(MID_SLOT_FLOOR, pageGap) - 1;

  if (page <= MID_SLOT_CEIL) {
    return [...Array.from(new Array(page - 1), (_, i) => i + 1)];
  }

  if (page > placesForLeft) {
    return [
      1,
      null,
      ...Array.from(
        new Array(placesForLeft - 2),
        (_, i) => page - (placesForLeft - 2) + i
      ),
    ];
  }

  return [...Array.from(new Array(page), (_, i) => i + 1)];
};

const getRightPageNumbers = (page: number, pages: number): MyArray => {
  const pageGap = pages - page;
  const placesForRight =
    PAGINATION_SLOTS - Math.min(MID_SLOT_FLOOR, page - 1) - 1;

  if (page + MID_SLOT_CEIL > pages) {
    return [...Array.from(new Array(pageGap), (_, i) => page + 1 + i)];
  }

  if (pageGap > MID_SLOT_FLOOR) {
    if (page + MID_SLOT_FLOOR < pages) {
      return [
        ...Array.from(new Array(placesForRight - 2), (_, i) => page + i + 1),
        null,
        pages,
      ];
    }

    return [...Array.from(new Array(pageGap), (_, i) => i + page + 2)];
  }

  return [];
};

const getPageNumbers = (page: number, pages: number) => {
  return Array.from(
    pages > PAGINATION_SLOTS
      ? [
          ...getLeftPageNumbers(page, pages),
          page,
          ...getRightPageNumbers(page, pages),
        ]
      : [...Array.from(new Array(pages), (_, i) => i + 1)]
  );
};

export const Pagination: FC<Props> = ({
  loading,
  page,
  pageCount,
  hasPrevious,
  hasNext,
  onChange,
}) => {
  if (pageCount <= 1) return null;

  return (
    <div className="flex flex-row justify-around gap-x-4">
      <Button
        disabled={loading || !hasPrevious}
        onClick={() => onChange(page - 1)}
      >
        <Icon icon="Arrow-left" size={24} />
      </Button>

      <div className="flex items-center">
        {getPageNumbers(page, pageCount).map((p, i) => (
          <button
            type="button"
            key={`page-${i}`}
            className={classNames("px-2 text-center w-8 inline-block", {
              "font-black": p && p === page,
              "hover:font-black hover:cursor-pointer": p,
            })}
            onClick={(): void => {
              if (p) onChange(p);
            }}
          >
            {p || "..."}
          </button>
        ))}
      </div>

      <Button disabled={loading || !hasNext} onClick={() => onChange(page + 1)}>
        <Icon icon="Arrow-right" size={24} />
      </Button>
    </div>
  );
};
